import { NotificationService } from 'src/app/shared/services/notification.service';
import { inject, Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmationService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { forkJoin, Observable, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import { BaseServices } from 'src/app/kuba/kuba.services';
import { AppraisalMeetingService } from '../../services/appraisal-meeting.service';
import { AppraisalTaskService } from '../../../tasks/services/appraisal-task.service';
import { AppraisalSettingsService } from '../../../settings/services/appraisal-settings.service';
import { AppraisalRelationshipStatus } from '../../../settings/models/appraisal-relationship-status.enum';
import {
  AppraisalMeetingDto,
  AppraisalMeetingEmployeeProgressStatus,
  AppraisalMeetingQuestionDto,
  AppraisalMeetingSectionDto,
  AppraisalMeetingResponseSubmitDto,
  AppraisalMeetingRespondentRole,
  AppraisalMeetingStatus,
  AppraisalMeetingUpdateDto
} from '../../models';
import {
  AppraisalTaskCreateDto,
  AppraisalTaskDto,
  AppraisalTaskStatus,
  AppraisalTaskUpdateDto
} from '../../../tasks/models';

@Component({
  selector: 'appraisal-meeting-detail',
  templateUrl: './appraisal-meeting-detail.component.html',
  styleUrls: ['./appraisal-meeting-detail.component.scss'],
  standalone: false
})
export class AppraisalMeetingDetailComponent implements OnInit {
  private readonly notifications = inject(NotificationService);

  meeting: AppraisalMeetingDto | null = null;
  isLoading = false;
  isSaving = false;
  isLoadingTasks = false;
  isSubmittingTask = false;
  showTaskDialog = false;
  taskActionStates: Record<number, 'completing' | 'deleting'> = {};

  // Caller context
  isAdmin = false;
  isManager = false;
  isEmployee = false;
  isHistoricalManagerViewer = false;
  currentUserId: number;

  // Pending response drafts keyed by questionId
  responseDrafts: Record<number, AppraisalMeetingResponseSubmitDto> = {};
  expandedSections: Record<number, boolean> = {};
  employeeSectionRequiredCompletion: Record<number, boolean> = {};
  showComparedResponses: Record<number, boolean> = {};
  showValidationErrors = false;
  touchedQuestions = new Set<number>();

  meetingTasks: AppraisalTaskDto[] = [];
  taskAssignees: { id: number; name: string }[] = [];
  taskDialogQuestion: AppraisalMeetingQuestionDto | null = null;
  editingTaskId: number | null = null;
  editingTaskOriginalDueDate = '';
  readonly minTaskDueDate = this.toDateInputValue(new Date().toISOString());
  newTask: {
    QuestionId: number | null;
    Title: string;
    Description: string;
    DueDate: string;
    AssignedToUserId: number | null;
  } = {
    QuestionId: null,
    Title: '',
    Description: '',
    DueDate: '',
    AssignedToUserId: null
  };

  AppraisalMeetingStatus = AppraisalMeetingStatus;
  AppraisalMeetingEmployeeProgressStatus = AppraisalMeetingEmployeeProgressStatus;
  AppraisalMeetingRespondentRole = AppraisalMeetingRespondentRole;
  AppraisalTaskStatus = AppraisalTaskStatus;
  readonly taskDescriptionPreviewLimit = 72;
  private readonly timezoneSuffixPattern = /(z|[+-]\d{2}:?\d{2})$/i;
  likertToneStyles: Record<number, Record<string, string>> = {
    1: { '--likert-bg': '#fee2e2', '--likert-color': '#b91c1c', '--likert-border': '#ef4444' },
    2: { '--likert-bg': '#ffedd5', '--likert-color': '#c2410c', '--likert-border': '#f97316' },
    3: { '--likert-bg': '#fef3c7', '--likert-color': '#b45309', '--likert-border': '#f59e0b' },
    4: { '--likert-bg': '#ecfccb', '--likert-color': '#4d7c0f', '--likert-border': '#84cc16' },
    5: { '--likert-bg': '#dcfce7', '--likert-color': '#166534', '--likert-border': '#22c55e' }
  };

  statusOptions = [
    { labelKey: 'APPRAISAL_STATUS_PRE_MEETING', value: AppraisalMeetingStatus.InProgress },
    { labelKey: 'APPRAISAL_STATUS_MEETING_ACCEPTED', value: AppraisalMeetingStatus.PendingReview },
    { labelKey: 'APPRAISAL_STATUS_MEETING_COMPLETED', value: AppraisalMeetingStatus.Completed },
    { labelKey: 'APPRAISAL_STATUS_CANCELLED_MEETING', value: AppraisalMeetingStatus.Cancelled }
  ];

  // Edit mode
  isEditing = false;
  isUpdating = false;
  isUpdatingStatus = false;
  pendingStatusValue: AppraisalMeetingStatus | null = null;
  isResendingEmail = false;
  isDeletingMeeting = false;
  isSigning = false;
  editTitle = '';
  editScheduledDate = '';
  editEndTime = '';
  editNotes = '';
  editManagerPrivateNotes = '';
  editEmployeeUserId: number | null = null;
  employeeOptions: { label: string; value: number }[] = [];
  minScheduledDate = '';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private meetingService: AppraisalMeetingService,
    private taskService: AppraisalTaskService,
    private settingsService: AppraisalSettingsService,
    private confirmationService: ConfirmationService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.resolveCallerContext();
    const id = +this.route.snapshot.params['id'];
    this.loadMeeting(id);
  }

  private resolveCallerContext(): void {
    const role = BaseServices.UserRole;
    this.currentUserId = +BaseServices.UserId;
    this.isAdmin = role === 'Admin';
  }

  private loadMeeting(id: number): void {
    this.isLoading = true;
    this.isHistoricalManagerViewer = false;
    this.meetingService.getById(id).subscribe({
      next: meeting => {
        if (!meeting) {
          this.isLoading = false;
          this.isUpdatingStatus = false;
          this.pendingStatusValue = null;
          this.isHistoricalManagerViewer = false;
          this.navigateToAppraisalList();
          return;
        }
        this.meeting = meeting;
        this.isManager = meeting.ManagerUserId === this.currentUserId;
        this.isEmployee = meeting.EmployeeUserId === this.currentUserId;
        this.isHistoricalManagerViewer =
          !!meeting.IsReadOnlyForCurrentUser &&
          !this.isAdmin &&
          !this.isManager &&
          !this.isEmployee;
        this.initResponseDrafts(meeting);
        this.initExpandedSections(meeting);
        this.initEmployeeSectionCompletionState();
        this.initTaskAssignees(meeting);
        this.loadMeetingTasks(meeting.Id);
        this.isUpdatingStatus = false;
        this.pendingStatusValue = null;
        this.isLoading = false;
      },
      error: () => {
        this.isUpdatingStatus = false;
        this.pendingStatusValue = null;
        this.isLoading = false;
        this.meetingTasks = [];
        this.isHistoricalManagerViewer = false;
      }
    });
  }

  private initTaskAssignees(meeting: AppraisalMeetingDto): void {
    const options = [
      { id: meeting.ManagerUserId, name: meeting.ManagerName || 'Manager' },
      { id: meeting.EmployeeUserId, name: meeting.EmployeeName || 'Employee' }
    ];

    const seen = new Set<number>();
    this.taskAssignees = options.filter(option => {
      if (seen.has(option.id)) return false;
      seen.add(option.id);
      return true;
    });
    this.resetTaskDraft();
  }

  private loadMeetingTasks(meetingId: number): void {
    this.isLoadingTasks = true;
    this.taskService.getByMeetingId(meetingId).subscribe({
      next: tasks => {
        this.meetingTasks = [...tasks].sort((a, b) =>
          new Date(b.CreatedOn).getTime() - new Date(a.CreatedOn).getTime()
        );
        this.taskActionStates = {};
        this.isLoadingTasks = false;
      },
      error: () => {
        this.meetingTasks = [];
        this.taskActionStates = {};
        this.isLoadingTasks = false;
      }
    });
  }

  private resetTaskDraft(): void {
    this.editingTaskId = null;
    this.editingTaskOriginalDueDate = '';
    this.newTask = {
      QuestionId: null,
      Title: '',
      Description: '',
      DueDate: '',
      AssignedToUserId: this.taskAssignees[0]?.id ?? null
    };
  }

  private initResponseDrafts(meeting: AppraisalMeetingDto): void {
    this.responseDrafts = {};
    const myRole = this.getCurrentRespondentRole();

    if (myRole === null) {
      return;
    }

    meeting.Sections.forEach(section => {
      section.Questions.forEach(question => {
        const existing = question.Responses.find(r => r.RespondentRole === myRole);
        this.responseDrafts[question.QuestionId] = existing
          ? { ...existing }
          : { QuestionId: question.QuestionId, RespondentRole: myRole };
      });
    });
  }

  private initExpandedSections(meeting: AppraisalMeetingDto): void {
    const visibleSections = [...meeting.Sections]
      .filter(section => this.canSeeSection(section))
      .sort((a, b) => a.SortOrder - b.SortOrder);

    if (visibleSections.length === 0) {
      this.expandedSections = {};
      return;
    }

    const defaultSectionId = this.isEmployee
      ? visibleSections[0].SectionId
      : (visibleSections.find(section =>
          this.getSectionAnsweredCount(section) < this.getVisibleQuestions(section).length
        )?.SectionId ?? visibleSections[0].SectionId);

    const nextState: Record<number, boolean> = {};
    visibleSections.forEach(section => {
      nextState[section.SectionId] = this.isEmployee
        ? section.SectionId === defaultSectionId
        : ( this.expandedSections[section.SectionId] ?? section.SectionId === defaultSectionId);
    });

    if (!Object.values(nextState).some(Boolean)) {
      nextState[defaultSectionId] = true;
    }

    this.expandedSections = nextState;
  }

  private initEmployeeSectionCompletionState(): void {
    const nextState: Record<number, boolean> = {};

    this.getVisibleSections().forEach(section => {
      nextState[section.SectionId] = this.areEmployeeRequiredQuestionsAnsweredInSection(section);
    });

    this.employeeSectionRequiredCompletion = nextState;
  }

  getCurrentRespondentRole(): AppraisalMeetingRespondentRole | null {
    if (this.isManager) {
      return AppraisalMeetingRespondentRole.Manager;
    }

    if (this.isEmployee) {
      return AppraisalMeetingRespondentRole.Employee;
    }

    return null;
  }

  getQuestionResponse(
    question: AppraisalMeetingQuestionDto,
    role: AppraisalMeetingRespondentRole
  ): AppraisalMeetingResponseSubmitDto | undefined {
    const currentRole = this.getCurrentRespondentRole();

    if (currentRole !== null && role === currentRole) {
      return this.responseDrafts[question.QuestionId];
    }

    return question.Responses.find(response => response.RespondentRole === role);
  }

  shouldShowResponseBlock(
    question: AppraisalMeetingQuestionDto,
    role: AppraisalMeetingRespondentRole
  ): boolean {
    const currentRole = this.getCurrentRespondentRole();
    const responseEnabled = this.isResponseEnabledForRole(question, role);

    if (currentRole !== null && role === currentRole) {
      return responseEnabled;
    }

    const storedResponse = question.Responses.find(response => response.RespondentRole === role);
    return ( responseEnabled &&
      (this.hasResponseValue(storedResponse) || this.canInspectPendingResponses()));
  }

  canEditResponseBlock(
    question: AppraisalMeetingQuestionDto,
    role: AppraisalMeetingRespondentRole
  ): boolean {
    return ( this.getCurrentRespondentRole() === role && this.canRespondAsRole(question, role));
  }

  getResponseRoleLabel(role: AppraisalMeetingRespondentRole): string {
    return role === AppraisalMeetingRespondentRole.Manager ? 'MANAGER' : 'EMPLOYEE';
  }

  getPrimaryResponseRole(
    question: AppraisalMeetingQuestionDto
  ): AppraisalMeetingRespondentRole | null {
    const currentRole = this.getCurrentRespondentRole();

    if (currentRole !== null && this.shouldShowResponseBlock(question, currentRole)) {
      return currentRole;
    }

    if (this.shouldShowResponseBlock(question, AppraisalMeetingRespondentRole.Manager)) {
      return AppraisalMeetingRespondentRole.Manager;
    }

    if (this.shouldShowResponseBlock(question, AppraisalMeetingRespondentRole.Employee)) {
      return AppraisalMeetingRespondentRole.Employee;
    }

    return null;
  }

  getSecondaryResponseRole(
    question: AppraisalMeetingQuestionDto
  ): AppraisalMeetingRespondentRole | null {
    const primaryRole = this.getPrimaryResponseRole(question);

    if (primaryRole === null) {
      return null;
    }

    const secondaryRole = primaryRole === AppraisalMeetingRespondentRole.Manager
      ? AppraisalMeetingRespondentRole.Employee
      : AppraisalMeetingRespondentRole.Manager;

    return this.shouldShowResponseBlock(question, secondaryRole) ? secondaryRole : null;
  }

  isPrimaryDisplayedRole(
    question: AppraisalMeetingQuestionDto,
    role: AppraisalMeetingRespondentRole
  ): boolean {
    return this.getPrimaryResponseRole(question) === role;
  }

  toggleCompareResponses(questionId: number): void {
    this.showComparedResponses[questionId] = !this.showComparedResponses[questionId];
  }

  isCompareResponsesVisible(questionId: number): boolean {
    return !!this.showComparedResponses[questionId];
  }

  private hasResponseValue(response?: AppraisalMeetingResponseSubmitDto): boolean {
    if (!response) {
      return false;
    }

    return (
      !!response.TextAnswer?.trim() ||
      response.YesNoAnswer === true ||
      response.YesNoAnswer === false ||
      (response.LikertAnswer !== null && response.LikertAnswer !== undefined)
    );
  }

  private isResponseEnabledForRole(
    question: AppraisalMeetingQuestionDto,
    role: AppraisalMeetingRespondentRole
  ): boolean {
    return role === AppraisalMeetingRespondentRole.Manager
      ? question.ManagerCanAnswer && question.IsManagerVisible
      : question.EmployeeCanAnswer && question.IsEmployeeVisible;
  }

  private canRespondAsRole(
    question: AppraisalMeetingQuestionDto,
    role: AppraisalMeetingRespondentRole
  ): boolean {
    return ( this.canEditResponsesForMeeting() && this.isResponseEnabledForRole(question, role));
  }

  private canInspectPendingResponses(): boolean {
    return this.canViewMeetingAsManager();
  }

  private canViewMeetingAsManager(): boolean {
    return this.isAdmin || this.isManager || this.isHistoricalManagerViewer;
  }

  private isRequiredEmployeeQuestion(question: AppraisalMeetingQuestionDto): boolean {
    return ( question.IsRequired &&
      this.isResponseEnabledForRole(question, AppraisalMeetingRespondentRole.Employee));
  }

  private isCurrentRespondentEmployee(): boolean {
    return ( this.getCurrentRespondentRole() === AppraisalMeetingRespondentRole.Employee
    );
  }

  private isQuestionAnswerableByCurrentRespondent(question: AppraisalMeetingQuestionDto): boolean {
    const currentRole = this.getCurrentRespondentRole();
    if (currentRole === null) {
      return false;
    }

    return this.canRespondAsRole(question, currentRole);
  }

  private isQuestionRequiredForEmployeeRespondent(question: AppraisalMeetingQuestionDto): boolean {
    return ( this.isCurrentRespondentEmployee() &&
      this.isRequiredEmployeeQuestion(question) &&
      this.canRespondAsRole(question, AppraisalMeetingRespondentRole.Employee));
  }

  private getCurrentRespondentQuestions(): AppraisalMeetingQuestionDto[] {
    if (!this.meeting) {
      return [];
    }

    return this.meeting.Sections
      .flatMap(section => section.Questions || [])
      .filter(question => this.isQuestionAnswerableByCurrentRespondent(question));
  }

  canAnswer(question: AppraisalMeetingQuestionDto): boolean {
    return this.isQuestionAnswerableByCurrentRespondent(question);
  }

  canSeeQuestion(question: AppraisalMeetingQuestionDto): boolean {
    if (this.canViewMeetingAsManager()) {
      return ( question.IsManagerVisible ||
        question.IsEmployeeVisible ||
        question.ManagerCanAnswer ||
        question.EmployeeCanAnswer
      );
    }
    if (this.isEmployee) return question.IsEmployeeVisible;
    return false;
  }

  canSeeSection(section: AppraisalMeetingSectionDto): boolean {
    if (this.canViewMeetingAsManager()) {
      return section.Questions.some(q => this.canSeeQuestion(q));
    }
    return section.Questions.some(q => q.IsEmployeeVisible);
  }

  isMeetingLocked(): boolean {
    if (!this.meeting) return true;
    if (this.meeting.IsArchived) return true;
    return (
      this.meeting.Status === AppraisalMeetingStatus.Completed ||
      this.meeting.Status === AppraisalMeetingStatus.Cancelled
    );
  }

  canEditResponsesForMeeting(): boolean {
    return ( !!this.meeting &&
      !this.isMeetingLocked() &&
      this.meeting.Status !== AppraisalMeetingStatus.Draft
    );
  }

  getVisibleSections(): AppraisalMeetingSectionDto[] {
    if (!this.meeting?.Sections?.length) return [];
    return [...this.meeting.Sections]
      .filter(section => this.canSeeSection(section))
      .sort((a, b) => a.SortOrder - b.SortOrder);
  }

  getVisibleQuestions(section: AppraisalMeetingSectionDto): AppraisalMeetingQuestionDto[] {
    if (!section?.Questions?.length) return [];
    return [...section.Questions]
      .filter(question => this.canSeeQuestion(question))
      .sort((a, b) => a.SortOrder - b.SortOrder);
  }

  getQuestionTasks(questionId: number): AppraisalTaskDto[] {
    return this.meetingTasks.filter(task => task.QuestionId === questionId);
  }

  hasQuestionTasks(questionId: number): boolean {
    return this.getQuestionTasks(questionId).length > 0;
  }

  getSectionActionPointCount(section: AppraisalMeetingSectionDto): number {
    const questionIds = new Set(
      this.getVisibleQuestions(section).map(question => question.QuestionId)
    );

    return this.meetingTasks.filter(task =>
      task.QuestionId !== undefined &&
      task.QuestionId !== null &&
      questionIds.has(task.QuestionId)
    ).length;
  }

  hasSectionActionPoints(section: AppraisalMeetingSectionDto): boolean {
    return this.getSectionActionPointCount(section) > 0;
  }

  getSectionAnsweredCount(section: AppraisalMeetingSectionDto): number {
    return this.getVisibleQuestions(section)
      .filter(question => this.isQuestionAnsweredForViewer(question))
      .length;
  }

  private isQuestionAnsweredForViewer(question: AppraisalMeetingQuestionDto): boolean {
    const currentRole = this.getCurrentRespondentRole();

    if (currentRole !== null && this.isResponseEnabledForRole(question, currentRole)) {
      return this.hasResponseValue(this.getQuestionResponse(question, currentRole));
    }

    if (this.canInspectPendingResponses()) {
      if (this.isResponseEnabledForRole(question, AppraisalMeetingRespondentRole.Employee)) {
        return this.hasResponseValue(
          question.Responses.find(response => response.RespondentRole === AppraisalMeetingRespondentRole.Employee)
        );
      }

      if (this.isResponseEnabledForRole(question, AppraisalMeetingRespondentRole.Manager)) {
        return this.hasResponseValue(
          question.Responses.find(response => response.RespondentRole === AppraisalMeetingRespondentRole.Manager)
        );
      }
    }

    return question.Responses.some(response => this.hasResponseValue(response));
  }

  private getEmployeeAnswerableQuestions(
    section: AppraisalMeetingSectionDto
  ): AppraisalMeetingQuestionDto[] {
    return this.getVisibleQuestions(section).filter(
      question => this.isEmployee && this.canRespondAsRole(question, AppraisalMeetingRespondentRole.Employee)
    );
  }

  getSectionRequiredRemainingCount(section: AppraisalMeetingSectionDto): number {
    if (!this.isEmployee) {
      return 0;
    }

    return this.getEmployeeAnswerableQuestions(section).filter(
      question =>
        question.IsRequired &&
        !this.hasResponseValue(this.responseDrafts[question.QuestionId])
    ).length;
  }

  getSectionOptionalRemainingCount(section: AppraisalMeetingSectionDto): number {
    if (!this.isEmployee) {
      return 0;
    }

    return this.getEmployeeAnswerableQuestions(section).filter(
      question =>
        !question.IsRequired &&
        !this.hasResponseValue(this.responseDrafts[question.QuestionId])
    ).length;
  }

  getSectionState(
    section: AppraisalMeetingSectionDto
  ): 'neutral' | 'required-pending' | 'optional-left' | 'complete' {
    if (!this.isEmployee) {
      return 'neutral';
    }

    const answerableQuestions = this.getEmployeeAnswerableQuestions(section);
    if (answerableQuestions.length === 0) {
      return 'neutral';
    }

    if (this.getSectionRequiredRemainingCount(section) > 0) {
      return 'required-pending';
    }

    if (this.getSectionOptionalRemainingCount(section) > 0) {
      return 'optional-left';
    }

    return 'complete';
  }

  getSectionStateIcon(section: AppraisalMeetingSectionDto): string {
    switch (this.getSectionState(section)) {
      case 'required-pending':
        return 'fa-exclamation-triangle';
      case 'optional-left':
        return 'fa-pencil-square-o';
      case 'complete':
        return 'fa-check-circle';
      default:
        return 'fa-circle';
    }
  }

  getSectionStateLabelKey(section: AppraisalMeetingSectionDto): string {
    switch (this.getSectionState(section)) {
      case 'required-pending':
        return 'APPRAISAL_SECTION_REQUIRED_LEFT';
      case 'optional-left':
        return 'APPRAISAL_SECTION_OPTIONAL_LEFT';
      case 'complete':
        return 'APPRAISAL_SECTION_ALL_ANSWERED';
      default:
        return '';
    }
  }

  getSectionStateHelpKey(section: AppraisalMeetingSectionDto): string {
    switch (this.getSectionState(section)) {
      case 'required-pending':
        return 'APPRAISAL_SECTION_REQUIRED_LEFT_HELP';
      case 'optional-left':
        return 'APPRAISAL_SECTION_OPTIONAL_LEFT_HELP';
      case 'complete':
        return 'APPRAISAL_SECTION_ALL_ANSWERED_HELP';
      default:
        return '';
    }
  }

  getSectionStateCount(section: AppraisalMeetingSectionDto): number | null {
    switch (this.getSectionState(section)) {
      case 'required-pending':
        return this.getSectionRequiredRemainingCount(section);
      case 'optional-left':
        return this.getSectionOptionalRemainingCount(section);
      default:
        return null;
    }
  }

  private areEmployeeRequiredQuestionsAnsweredInSection(
    section: AppraisalMeetingSectionDto
  ): boolean {
    if (!this.isEmployee) {
      return false;
    }

    const requiredQuestions = this.getVisibleQuestions(section).filter(
      question => this.isQuestionRequiredForEmployeeRespondent(question)
    );

    return requiredQuestions.every(question =>
      this.hasResponseValue(this.responseDrafts[question.QuestionId])
    );
  }

  private openSection(sectionId: number): void {
    if (this.isEmployee) {
      const nextState: Record<number, boolean> = {};
      this.getVisibleSections().forEach(section => {
        nextState[section.SectionId] = section.SectionId === sectionId;
      });
      this.expandedSections = nextState;
      return;
    }

    this.expandedSections = {
      ...this.expandedSections,
      [sectionId]: true
    };
  }

  private findVisibleSectionByQuestionId(
    questionId: number
  ): AppraisalMeetingSectionDto | undefined {
    return this.getVisibleSections().find(section =>
      this.getVisibleQuestions(section).some(
        question => question.QuestionId === questionId
      )
    );
  }

  private handleEmployeeSectionProgress(questionId: number): void {
    if (!this.isEmployee) {
      return;
    }

    const section = this.findVisibleSectionByQuestionId(questionId);
    if (!section) {
      return;
    }

    const isCompleted = this.areEmployeeRequiredQuestionsAnsweredInSection(section);
    this.employeeSectionRequiredCompletion = {
      ...this.employeeSectionRequiredCompletion,
      [section.SectionId]: isCompleted
    };
  }

  toggleSection(sectionId: number): void {
    if (this.isSectionExpanded(sectionId)) {
      if (this.isEmployee) {
        return;
      }

      this.expandedSections = {
        ...this.expandedSections,
        [sectionId]: false
      };
      return;
    }

    this.openSection(sectionId);
  }

  toggleAllSections(): void {
    if (this.isEmployee) {
      return;
    }

    const shouldExpand = !this.areAllSectionsExpanded();
    const expandedSections = { ...this.expandedSections };

    this.getVisibleSections().forEach(section => {
      expandedSections[section.SectionId] = shouldExpand;
    });

    this.expandedSections = expandedSections;
  }

  areAllSectionsExpanded(): boolean {
    const sections = this.getVisibleSections();
    return (
      sections.length > 0 &&
      sections.every(section => this.isSectionExpanded(section.SectionId))
    );
  }

  isSectionExpanded(sectionId: number): boolean {
    return !!this.expandedSections[sectionId];
  }

  getSectionAnchorId(sectionId: number): string {
    return `meeting-section-${sectionId}`;
  }

  scrollToSection(sectionId: number): void {
    this.openSection(sectionId);

    setTimeout(() => {
      document
        .getElementById(this.getSectionAnchorId(sectionId))
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  private buildResponsePayload(): AppraisalMeetingResponseSubmitDto[] {
    const currentRole = this.getCurrentRespondentRole();
    if (currentRole === null) {
      return [];
    }

    return this.getCurrentRespondentQuestions()
      .map(question => {
        const draft = this.responseDrafts[question.QuestionId];

        return {
          QuestionId: question.QuestionId,
          TextAnswer: draft?.TextAnswer,
          YesNoAnswer: draft?.YesNoAnswer,
          LikertAnswer: draft?.LikertAnswer,
          RespondentRole: currentRole
        };
      });
  }

  onSaveResponses(): void {
    if (!this.meeting || this.isSaving || !this.canSubmitEmployeeResponses()) return;

    const unanswered = this.getUnansweredRequiredQuestions();
    if (unanswered.length > 0) {
      this.showValidationErrors = true;
      this.scrollToQuestion(unanswered[0].QuestionId);
      this.notifications.add({ severity: 'error', summary: this.translate.instant('REQUIRED'), detail: this.translate.instant('REQUIRED_QUESTIONS_UNANSWERED') });
      return;
    }
    this.showValidationErrors = false;

    this.isSaving = true;
    const submitRequests: Observable<void>[] = [
      this.meetingService.submitResponses(
        this.meeting.Id,
        this.buildResponsePayload()
      )
    ];

    forkJoin(submitRequests).subscribe({
      next: () => {
        this.isSaving = false;
        this.notifications.success('UI_NOTIFICATIONS.SAVED');
        this.navigateToAppraisalList();
      },
      error: () => {
        this.isSaving = false;
        
      }
    });
  }

  onSaveDraft(): void {
    if (!this.meeting || this.isSaving || !this.canSaveDraft()) return;

    this.showValidationErrors = false;
    this.isSaving = true;

    const saveRequests: Observable<void>[] = [];

    if (this.isManager || this.isEmployee) {
      saveRequests.push(
        this.meetingService.saveResponses(
          this.meeting.Id,
          this.buildResponsePayload()
        )
      );
    }

    if (saveRequests.length === 0) {
      this.isSaving = false;
      return;
    }

    forkJoin(saveRequests).subscribe({
      next: () => {
        this.isSaving = false;
        this.notifications.success('UI_NOTIFICATIONS.SAVED');

        if (this.isEmployee) {
          this.navigateToAppraisalList();
          return;
        }

        this.loadMeeting(this.meeting!.Id);
      },
      error: () => {
        this.isSaving = false;
        
      }
    });
  }

  onSaveManagerAnswers(): void {
    if (!this.meeting || this.isSaving || !this.canSaveManagerAnswers()) return;

    this.showValidationErrors = false;
    this.isSaving = true;

    this.meetingService
      .saveResponses(this.meeting.Id, this.buildResponsePayload())
      .subscribe({
        next: () => {
          this.isSaving = false;
          this.notifications.success('UI_NOTIFICATIONS.SAVED');
          this.navigateToAppraisalList();
        },
        error: () => {
          this.isSaving = false;
          
        }
      });
  }

  onSaveAndCompleteMeeting(): void {
    if (!this.canSaveAndCompleteMeeting()) {
      return;
    }

    this.confirmMeetingCompletion();
  }

  getUnansweredRequiredQuestions(): AppraisalMeetingQuestionDto[] {
    if (!this.isCurrentRespondentEmployee()) return [];

    const unanswered: AppraisalMeetingQuestionDto[] = [];

    for (const section of this.getVisibleSections()) {
      for (const question of this.getVisibleQuestions(section)) {
        if (!this.isQuestionRequiredForEmployeeRespondent(question)) continue;

        const draft = this.responseDrafts[question.QuestionId];
        if (!this.hasResponseValue(draft)) {
          unanswered.push(question);
        }
      }
    }

    return unanswered;
  }

  private scrollToQuestion(questionId: number): void {
    // Find the section containing this question and expand it
    for (const section of this.getVisibleSections()) {
      const found = this.getVisibleQuestions(section).find(q => q.QuestionId === questionId);
      if (found) {
        this.expandedSections[section.SectionId] = true;
        setTimeout(() => {
          document
            .getElementById(this.getSectionAnchorId(section.SectionId))
            ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
        break;
      }
    }
  }

  onStatusChange(status: AppraisalMeetingStatus): void {
    if (
      !this.meeting ||
      this.isUpdatingStatus ||
      this.isResendingEmail ||
      this.isDeletingMeeting ||
      this.isUpdating ||
      this.meeting.Status === status ||
      this.isStatusButtonDisabled(status)
    ) {
      return;
    }

    if (status === AppraisalMeetingStatus.Completed) {
      this.confirmMeetingCompletion();
      return;
    }

    this.confirmMeetingStatusChange(status);
  }

  onResendEmail(): void {
    if (
      !this.meeting ||
      this.isResendingEmail ||
      this.isDeletingMeeting ||
      this.isUpdatingStatus ||
      this.isPendingAccessMeeting()
    ) {
      return;
    }

    this.isResendingEmail = true;
    this.meetingService.resendEmail(this.meeting.Id).subscribe({
      next: () => {
        this.isResendingEmail = false;
        this.notifications.success('UI_NOTIFICATIONS.SAVED');
      },
      error: () => {
        this.isResendingEmail = false;
        
      }
    });
  }

  requiresSignature(): boolean {
    return !!this.meeting?.RequiresBothPartiesSignature;
  }

  hasCurrentUserSigned(): boolean {
    if (!this.meeting) return false;
    if (this.isManager) return !!this.meeting.ManagerSignedAt;
    if (this.isEmployee) return !!this.meeting.EmployeeSignedAt;
    return false;
  }

  canSignMeeting(): boolean {
    if (!this.meeting) return false;
    if (!this.requiresSignature()) return false;
    if (this.isMeetingLocked()) return false;
    if (!(this.isManager || this.isEmployee)) return false;
    return !this.hasCurrentUserSigned();
  }

  isBothPartiesSigned(): boolean {
    return !!this.meeting?.ManagerSignedAt && !!this.meeting?.EmployeeSignedAt;
  }

  onSign(): void {
    if (!this.meeting || this.isSigning || !this.canSignMeeting()) return;

    this.confirmationService.confirm({
      message: this.translate.instant('APPRAISAL_CONFIRM_SIGN_MEETING'),
      accept: () => {
        this.isSigning = true;
        this.meetingService.sign(this.meeting!.Id).subscribe({
          next: () => {
            this.isSigning = false;
            this.notifications.success('UI_NOTIFICATIONS.SAVED');
            this.loadMeeting(this.meeting!.Id);
          },
          error: () => {
            this.isSigning = false;
            
          }
        });
      }
    });
  }

  onDelete(): void {
    if (!this.meeting || this.isDeletingMeeting || this.isUpdatingStatus || this.isResendingEmail) return;

    const archiveName = this.meeting.EmployeeName || this.meeting.Title || '—';

    this.confirmationService.confirm({
      message: this.translate.instant('CONFIRM_ARCHIVE_APPRAISAL', { name: archiveName }),
      accept: () => {
        this.isDeletingMeeting = true;
        this.meetingService.delete(this.meeting!.Id).subscribe({
          next: () => { this.notifications.success('UI_NOTIFICATIONS.DELETED'); this.navigateToAppraisalList(); },
          error: () => {
            this.isDeletingMeeting = false;
            
          }
        });
      }
    });
  }

  canManageFollowUpTasks(): boolean {
    return !!this.meeting && (this.isAdmin || this.isManager);
  }

  canCreateFollowUpTasks(): boolean {
    return ( !!this.meeting &&
      this.canManageFollowUpTasks() &&
      (this.meeting.Status === AppraisalMeetingStatus.PendingReview ||
        this.meeting.Status === AppraisalMeetingStatus.Completed));
  }

  canEditFollowUpTasks(): boolean {
    return ( !!this.meeting &&
      this.canManageFollowUpTasks() &&
      this.meeting.Status !== AppraisalMeetingStatus.Cancelled &&
      this.meeting.Status !== AppraisalMeetingStatus.InProgress
    );
  }

  hasFollowUpTasks(): boolean {
    return this.meetingTasks.length > 0;
  }

  shouldShowFollowUpTasksPanel(): boolean {
    return ( this.isLoadingTasks ||
      this.hasFollowUpTasks() ||
      this.canCreateFollowUpTasks() ||
      this.showTaskCreationInfo());
  }

  openTaskDialog(question?: AppraisalMeetingQuestionDto, task?: AppraisalTaskDto): void {
    if (task && (!this.canEditFollowUpTasks() || this.isTaskActionPending(task.Id))) {
      return;
    }

    if (!task && !this.canCreateFollowUpTasks()) {
      return;
    }

    this.resetTaskDraft();

    if (task) {
      this.editingTaskId = task.Id;
      this.taskDialogQuestion = question ?? this.findQuestionById(task.QuestionId);
      this.editingTaskOriginalDueDate = this.toDateInputValue(task.DueDate);
      this.newTask = {
        QuestionId: task.QuestionId ?? null,
        Title: task.Title || '',
        Description: task.Description || '',
        DueDate: this.toDateInputValue(task.DueDate),
        AssignedToUserId: task.AssignedToUserId
      };
    } else {
      this.taskDialogQuestion = question ?? null;
      this.newTask.QuestionId = question?.QuestionId ?? null;
    }

    this.showTaskDialog = true;
  }

  closeTaskDialog(): void {
    this.showTaskDialog = false;
    this.taskDialogQuestion = null;
    this.resetTaskDraft();
  }

  isTaskEditMode(): boolean {
    return this.editingTaskId !== null;
  }

  getTaskDialogHeaderKey(): string {
    return this.isTaskEditMode()
      ? 'APPRAISAL_ACTION_POINT'
      : 'APPRAISAL_ADD_ACTION_POINT';
  }

  getTaskDialogSubmitKey(): string {
    return this.isTaskEditMode()
      ? 'SAVE'
      : 'APPRAISAL_ADD_ACTION_POINT';
  }

  submitTaskDialog(): void {
    if (this.isTaskEditMode()) {
      this.updateFollowUpTask();
      return;
    }

    this.createFollowUpTask();
  }

  createFollowUpTask(): void {
    if (!this.meeting || !this.canCreateFollowUpTasks() || this.isSubmittingTask) {
      return;
    }

    const title = (this.newTask.Title || '').trim();
    if (!title || !this.newTask.AssignedToUserId) {
      this.notifications.success('UI_NOTIFICATIONS.DELETED');
      return;
    }

    if (this.isDueDateSelectionInvalid()) {
      this.notifications.success('UI_NOTIFICATIONS.DELETED');
      return;
    }

    this.isSubmittingTask = true;

    const dto: AppraisalTaskCreateDto = {
      AppraisalMeetingId: this.meeting.Id,
      QuestionId: this.newTask.QuestionId ?? undefined,
      AssignedToUserId: this.newTask.AssignedToUserId,
      Title: title,
      Description: (this.newTask.Description || '').trim() || undefined,
      DueDate: this.newTask.DueDate
        ? new Date(this.newTask.DueDate).toISOString()
        : undefined
    };

    this.taskService.create(dto).subscribe({
      next: () => {
        this.isSubmittingTask = false;
        this.closeTaskDialog();
        this.notifications.success('UI_NOTIFICATIONS.SAVED');
        this.loadMeetingTasks(this.meeting!.Id);
      },
      error: () => {
        this.isSubmittingTask = false;
        
      }
    });
  }

  updateFollowUpTask(): void {
    if (!this.meeting || !this.canEditFollowUpTasks() || !this.editingTaskId || this.isSubmittingTask) {
      return;
    }

    const title = (this.newTask.Title || '').trim();
    if (!title || !this.newTask.AssignedToUserId) {
      this.notifications.success('UI_NOTIFICATIONS.DELETED');
      return;
    }

    if (this.isDueDateSelectionInvalid(this.editingTaskOriginalDueDate)) {
      this.notifications.success('UI_NOTIFICATIONS.DELETED');
      return;
    }

    this.isSubmittingTask = true;

    const dto: AppraisalTaskUpdateDto = {
      Title: title,
      Description: (this.newTask.Description || '').trim() || undefined,
      DueDate: this.newTask.DueDate
        ? new Date(this.newTask.DueDate).toISOString()
        : undefined,
      AssignedToUserId: this.newTask.AssignedToUserId
    };

    this.taskService.update(this.editingTaskId, dto).subscribe({
      next: () => {
        this.isSubmittingTask = false;
        this.closeTaskDialog();
        this.notifications.success('UI_NOTIFICATIONS.SAVED');
        this.loadMeetingTasks(this.meeting!.Id);
      },
      error: () => {
        this.isSubmittingTask = false;
        
      }
    });
  }

  canCompleteTask(task: AppraisalTaskDto): boolean {
    if (this.isHistoricalManagerViewer) {
      return false;
    }

    return (
      task.Status !== AppraisalTaskStatus.Done &&
      (this.isAdmin || this.isManager || task.AssignedToUserId === this.currentUserId)
    );
  }

  completeTask(task: AppraisalTaskDto): void {
    if (!this.meeting || !this.canCompleteTask(task) || this.isTaskActionPending(task.Id)) return;

    this.setTaskActionState(task.Id, 'completing');
    this.taskService.updateStatus(task.Id, { Status: AppraisalTaskStatus.Done }).subscribe({
      next: () => {
        this.loadMeetingTasks(this.meeting!.Id);
        this.notifications.success('UI_NOTIFICATIONS.SAVED');
      },
      error: () => {
        this.clearTaskActionState(task.Id);
        
      }
    });
  }

  canDeleteTask(task?: AppraisalTaskDto): boolean {
    return (
      (this.isAdmin || this.isManager) &&
      (!task || !this.isTaskActionPending(task.Id)));
  }

  canOpenTaskChip(task?: AppraisalTaskDto): boolean {
    return ( this.canEditFollowUpTasks() &&
      (!task || !this.isTaskActionPending(task.Id)));
  }

  deleteTask(task: AppraisalTaskDto): void {
    if (!this.meeting || !this.canDeleteTask(task)) return;
    this.confirmationService.confirm({
      message: this.translate.instant('APPRAISAL_CONFIRM_DELETE_ACTION_POINT'),
      accept: () => {
        this.setTaskActionState(task.Id, 'deleting');
        this.taskService.delete(task.Id).subscribe({
          next: () => {
            this.loadMeetingTasks(this.meeting!.Id);
            this.notifications.success('UI_NOTIFICATIONS.SAVED');
          },
          error: () => {
            this.clearTaskActionState(task.Id);
            
          }
        });
      }
    });
  }

  isTaskActionPending(taskId: number): boolean {
    return !!this.taskActionStates[taskId];
  }

  isTaskDeleting(taskId: number): boolean {
    return this.taskActionStates[taskId] === 'deleting';
  }

  isTaskCompleting(taskId: number): boolean {
    return this.taskActionStates[taskId] === 'completing';
  }

  private setTaskActionState(taskId: number, state: 'completing' | 'deleting'): void {
    this.taskActionStates = {
      ...this.taskActionStates,
      [taskId]: state
    };
  }

  private clearTaskActionState(taskId: number): void {
    const { [taskId]: _removed, ...rest } = this.taskActionStates;
    this.taskActionStates = rest;
  }

  getTaskStatusLabel(status: number): string {
    const statusMap: Record<number, string> = {
      [AppraisalTaskStatus.Open]: 'OPEN',
      [AppraisalTaskStatus.InProgress]: 'IN_PROGRESS',
      [AppraisalTaskStatus.Done]: 'DONE'
    };

    return statusMap[status] ?? 'OPEN';
  }

  getTaskDescriptionPreview(description?: string): string {
    const normalizedDescription = (description || '').replace(/\s+/g, ' ').trim();
    if (!normalizedDescription) {
      return '—';
    }

    if (normalizedDescription.length <= this.taskDescriptionPreviewLimit) {
      return normalizedDescription;
    }

    return `${normalizedDescription.slice(0, this.taskDescriptionPreviewLimit).trim()}...`;
  }

  hasTruncatedTaskDescription(description?: string): boolean {
    return ( ((description || '').replace(/\s+/g, ' ').trim().length || 0) > this.taskDescriptionPreviewLimit
    );
  }

  getTaskStatusClass(status: number): string {
    const statusMap: Record<number, string> = {
      [AppraisalTaskStatus.Open]: 'badge-secondary',
      [AppraisalTaskStatus.InProgress]: 'badge-primary',
      [AppraisalTaskStatus.Done]: 'badge-success'
    };

    return statusMap[status] ?? 'badge-secondary';
  }

  getStatusLabel(status: number): string {
    const statusMap: Record<number, string> = {
      [AppraisalMeetingStatus.Draft]: 'APPRAISAL_STATUS_PENDING_ACCESS',
      [AppraisalMeetingStatus.InProgress]: 'APPRAISAL_STATUS_PRE_MEETING',
      [AppraisalMeetingStatus.PendingReview]: 'APPRAISAL_STATUS_MEETING_ACCEPTED',
      [AppraisalMeetingStatus.Completed]: 'APPRAISAL_STATUS_MEETING_COMPLETED',
      [AppraisalMeetingStatus.Cancelled]: 'APPRAISAL_STATUS_CANCELLED_MEETING'
    };
    return statusMap[status] ?? 'UNKNOWN';
  }

  getMeetingStatusHelpKey(status: number): string {
    const statusMap: Record<number, string> = {
      [AppraisalMeetingStatus.Draft]: 'APPRAISAL_MEETING_STATUS_PENDING_ACCESS_HELP',
      [AppraisalMeetingStatus.InProgress]: 'APPRAISAL_MEETING_STATUS_PRE_MEETING_HELP',
      [AppraisalMeetingStatus.PendingReview]: 'APPRAISAL_MEETING_STATUS_PENDING_REVIEW_HELP',
      [AppraisalMeetingStatus.Completed]: 'APPRAISAL_MEETING_STATUS_COMPLETED_HELP',
      [AppraisalMeetingStatus.Cancelled]: 'APPRAISAL_MEETING_STATUS_CANCELLED_HELP'
    };

    return statusMap[status] ?? 'APPRAISAL_MEETING_STATUS_PRE_MEETING_HELP';
  }

  getStatusClass(status: number): string {
    const map: Record<number, string> = {
      [AppraisalMeetingStatus.Draft]: 'tone-pending-access',
      [AppraisalMeetingStatus.InProgress]: 'tone-pre-meeting',
      [AppraisalMeetingStatus.PendingReview]: 'tone-pending-review',
      [AppraisalMeetingStatus.Completed]: 'tone-completed',
      [AppraisalMeetingStatus.Cancelled]: 'tone-cancelled'
    };
    return map[status] ?? 'tone-pre-meeting';
  }

  getParticipantDisplayName(name?: string | null, userId?: number | null): string {
    const trimmedName = name?.trim();
    if (trimmedName) {
      return trimmedName;
    }

    return userId ? `#${userId}` : '—';
  }

  getParticipantStatusLabelKey(status?: number | null): string | null {
    if (status === 0) {
      return 'DELETED';
    }

    if (status === 2) {
      return 'INACTIVE';
    }

    return null;
  }

  getParticipantStatusClass(status?: number | null): string {
    return status === 0 ? 'badge-danger' : 'badge-warning';
  }

  getStatusButtonClass(status: number): string {
    const isActive = this.meeting?.Status === status;
    return `${this.getStatusClass(status)} ${isActive ? 'is-active' : 'is-inactive'}`;
  }

  isPendingAccessMeeting(): boolean {
    return this.meeting?.Status === AppraisalMeetingStatus.Draft;
  }

  hasActiveManagerRelationship(): boolean {
    return ( this.meeting?.ManagerRelationshipStatus === AppraisalRelationshipStatus.Active
    );
  }

  getPendingAccessBannerKey(): string {
    return this.hasActiveManagerRelationship()
      ? 'APPRAISAL_MEETING_READY_TO_START_AFTER_ACCESS'
      : 'APPRAISAL_MEETING_WAITING_FOR_ACCESS';
  }

  isStatusButtonDisabled(status: AppraisalMeetingStatus): boolean {
    if (!this.meeting) {
      return true;
    }

    if ((this.isMeetingLocked() && this.meeting.Status !== status) || this.isMeetingActionPending()) {
      return true;
    }

    if (!this.isPendingAccessMeeting()) {
      if (
        status === AppraisalMeetingStatus.Completed &&
        !this.hasEmployeeCompletedRequiredQuestions()
      ) {
        return true;
      }

      if (
        status === AppraisalMeetingStatus.Completed &&
        this.requiresSignature() &&
        !this.isBothPartiesSigned()
      ) {
        return true;
      }

      return false;
    }

    if (status === AppraisalMeetingStatus.Cancelled) {
      return false;
    }

    if (status !== AppraisalMeetingStatus.InProgress) {
      return true;
    }

    return !this.hasActiveManagerRelationship();
  }

  getStatusButtonTitleKey(status: AppraisalMeetingStatus): string {
    if (this.isPendingAccessMeeting() && status !== AppraisalMeetingStatus.Cancelled) {
      if (status !== AppraisalMeetingStatus.InProgress) {
        return 'APPRAISAL_MEETING_START_BEFORE_NEXT_STATUS';
      }

      if (!this.hasActiveManagerRelationship()) {
        return 'APPRAISAL_MEETING_WAITING_FOR_ACCESS';
      }
    }

    if (
      status === AppraisalMeetingStatus.Completed &&
      !this.hasEmployeeCompletedRequiredQuestions()
    ) {
      return 'APPRAISAL_COMPLETE_REQUIRES_EMPLOYEE_REQUIRED';
    }

    if (
      status === AppraisalMeetingStatus.Completed &&
      this.requiresSignature() &&
      !this.isBothPartiesSigned()
    ) {
      return 'APPRAISAL_COMPLETE_REQUIRES_BOTH_SIGNATURES';
    }

    return this.getMeetingStatusHelpKey(status);
  }

  signatureBannerKey(): string | null {
    if (!this.meeting || !this.requiresSignature() || this.isMeetingLocked()) {
      return null;
    }

    if (this.isBothPartiesSigned()) {
      return this.isAdmin || this.isManager
        ? 'APPRAISAL_SIGNATURE_BOTH_SIGNED_READY'
        : 'APPRAISAL_SIGNATURE_BOTH_SIGNED';
    }

    if (this.isManager || this.isEmployee) {
      if (!this.hasCurrentUserSigned()) {
        return 'APPRAISAL_SIGNATURE_YOU_MUST_SIGN';
      }
      return 'APPRAISAL_SIGNATURE_WAITING_OTHER_PARTY';
    }

    return 'APPRAISAL_SIGNATURE_WAITING_BOTH_PARTIES';
  }

  signatureBannerSeverity(): 'info' | 'warning' | 'success' {
    if (!this.meeting) return 'info';
    if (this.isBothPartiesSigned()) return 'success';
    if ((this.isManager || this.isEmployee) && !this.hasCurrentUserSigned()) return 'warning';
    return 'info';
  }

  isMeetingActionPending(): boolean {
    return ( this.isUpdating || this.isUpdatingStatus || this.isResendingEmail || this.isDeletingMeeting || this.isSaving || this.isSigning
    );
  }

  onStartEdit(): void {
    if (!this.meeting || this.isMeetingLocked()) return;
    this.editTitle = this.meeting.Title;
    this.editScheduledDate = this.meeting.ScheduledDate
      ? this.toDatetimeLocalValue(this.meeting.ScheduledDate)
      : '';
    this.editEndTime = this.meeting.EndTime
      ? this.toDatetimeLocalValue(this.meeting.EndTime)
      : '';
    this.editNotes = this.meeting.Notes || '';
    this.editManagerPrivateNotes = this.meeting.ManagerPrivateNotes || '';
    this.editEmployeeUserId = this.meeting.EmployeeUserId;
    this.minScheduledDate = this.toDatetimeLocalNow();
    this.isEditing = true;
    this.loadEmployeeOptions();
  }

  onCancelEdit(): void {
    this.isEditing = false;
  }

  onSaveEdit(): void {
    if (!this.meeting || this.isUpdating || !this.editTitle.trim()) return;

    this.isUpdating = true;
    const dto: AppraisalMeetingUpdateDto = {
      Title: this.editTitle.trim(),
      ScheduledDate: this.editScheduledDate
        ? this.toIsoWithBrowserOffset(this.editScheduledDate)
        : undefined,
      EndTime: this.editEndTime
        ? this.toIsoWithBrowserOffset(this.editEndTime)
        : undefined,
      Notes: this.editNotes.trim() || undefined,
      ManagerPrivateNotes: this.editManagerPrivateNotes.trim() || undefined
    };

    if (this.editEmployeeUserId && this.editEmployeeUserId !== this.meeting.EmployeeUserId) {
      dto.EmployeeUserId = this.editEmployeeUserId;
    }

    this.meetingService.update(this.meeting.Id, dto).subscribe({
      next: () => {
        this.isUpdating = false;
        this.isEditing = false;
        this.loadMeeting(this.meeting!.Id);
        this.notifications.success('UI_NOTIFICATIONS.SAVED');
      },
      error: () => {
        this.isUpdating = false;
        
      }
    });
  }

  private loadEmployeeOptions(): void {
    if (!this.meeting) {
      this.employeeOptions = [];
      return;
    }

    this.settingsService
      .getMeetingEmployees(this.meeting.ManagerUserId, this.meeting.TemplateId)
      .subscribe({
        next: employees => {
          const employeeOptions = employees
            .map(e => ({
              label: e.Name && typeof e.Name === 'string' && e.Name.trim().length > 0
                ? e.Name.trim()
                : `Employee #${e.EmployeeUserId}`,
              value: e.EmployeeUserId
            }))
            .filter((emp, idx, arr) =>
              emp.label.length > 0 &&
              arr.findIndex(e => e.value === emp.value) === idx
            )
            .sort((a, b) => a.label.localeCompare(b.label));

          if (!employeeOptions.some(option => option.value === this.meeting!.EmployeeUserId)) {
            employeeOptions.unshift({
              label: this.meeting!.EmployeeName?.trim() || `Employee #${this.meeting!.EmployeeUserId}`,
              value: this.meeting!.EmployeeUserId
            });
          }

          this.employeeOptions = employeeOptions;
        },
        error: () => {
          this.employeeOptions = [];
        }
      });
  }

  private toDatetimeLocalNow(): string {
    const now = new Date();
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
  }

  private toDateInputValue(isoDate?: string): string {
    if (!isoDate) return '';

    const date = new Date(isoDate);
    if (isNaN(date.getTime())) return '';

    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  }

  private isDueDateSelectionInvalid(originalDueDate = ''): boolean {
    if (!this.newTask.DueDate) {
      return false;
    }

    if (this.newTask.DueDate >= this.minTaskDueDate) {
      return false;
    }

    return this.newTask.DueDate !== originalDueDate;
  }

  private toDatetimeLocalValue(isoDate: string): string {
    // Server returns ScheduledDate as a naive ISO (e.g. "2026-05-23T13:00:00") — that's the UTC
    // instant without the Z marker. Normalize to UTC first so getHours()/getMinutes() return the
    // browser's local wall-clock (15:00 Oslo for a 13:00Z meeting), which is what <input type=
    // "datetime-local"> expects.
    const d = new Date(this.toUtcTimestamp(isoDate) ?? isoDate);
    if (isNaN(d.getTime())) return '';
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }

  // Converts a <input type="datetime-local"> value ("YYYY-MM-DDTHH:mm") into an ISO string with
  // the browser's UTC offset appended (e.g. "2026-05-23T15:00:00+02:00"). Mirrors the create
  // component's composeLocalIso — without the offset the backend would deserialize as Unspecified
  // and downstream code (.ics formatter, email body) would misinterpret it.
  private toIsoWithBrowserOffset(datetimeLocal: string): string {
    const localDate = new Date(`${datetimeLocal}:00`);
    const offsetMinutes = -localDate.getTimezoneOffset();
    const sign = offsetMinutes >= 0 ? '+' : '-';
    const abs = Math.abs(offsetMinutes);
    const pad = (n: number) => String(n).padStart(2, '0');
    const offset = `${sign}${pad(Math.floor(abs / 60))}:${pad(abs % 60)}`;
    return `${datetimeLocal}:00${offset}`;
  }

  findQuestionById(questionId?: number): AppraisalMeetingQuestionDto | null {
    if (!questionId || !this.meeting?.Sections?.length) {
      return null;
    }

    for (const section of this.meeting.Sections) {
      const question = section.Questions?.find(item => item.QuestionId === questionId);
      if (question) {
        return question;
      }
    }

    return null;
  }

  getLikertGradeClass(grade: number, selectedGrade?: number | null): string {
    return selectedGrade === grade ? 'selected' : 'unselected';
  }

  getUnansweredRequiredCount(): number {
    return this.getUnansweredRequiredQuestions().length;
  }

  hasUnansweredRequired(): boolean {
    return this.getUnansweredRequiredCount() > 0;
  }

  canSaveDraft(): boolean {
    return ( !!this.meeting &&
      this.canEditResponsesForMeeting() &&
      this.isEmployee &&
      this.getCurrentRespondentQuestions().length > 0
    );
  }

  canSaveManagerAnswers(): boolean {
    return ( !!this.meeting &&
      this.isManager &&
      this.canEditResponsesForMeeting() &&
      this.getCurrentRespondentQuestions().length > 0
    );
  }

  canSaveAndCompleteMeeting(): boolean {
    return ( !!this.meeting &&
      this.isManager &&
      this.canEditResponsesForMeeting() &&
      this.hasEmployeeCompletedRequiredQuestions());
  }

  private hasEmployeeCompletedRequiredQuestions(): boolean {
    if (!this.meeting) {
      return false;
    }

    return ( this.meeting.EmployeeAnsweredRequiredQuestionCount >=
      this.meeting.EmployeeRequiredQuestionCount
    );
  }

  getResponseSaveLabelKey(): string {
    if (this.isEmployee) {
      return 'SAVE_FOR_LATER';
    }

    if (this.isManager) {
      return 'APPRAISAL_SAVE_RESPONSES_AND_CLOSE_MEETING';
    }

    return 'SAVE';
  }

  canSubmitEmployeeResponses(): boolean {
    return ( !!this.meeting &&
      this.isEmployee &&
      this.canEditResponsesForMeeting() &&
      this.getCurrentRespondentQuestions().length > 0
    );
  }

  getEmployeeProgressLabelKey(): string {
    if (!this.meeting) {
      return 'NOT_STARTED';
    }

    if (this.meeting.Status === AppraisalMeetingStatus.Completed) {
      return 'APPRAISAL_STATUS_MEETING_COMPLETED';
    }

    if (this.meeting.Status === AppraisalMeetingStatus.Cancelled) {
      return 'APPRAISAL_STATUS_CANCELLED_MEETING';
    }

    if (this.meeting.Status === AppraisalMeetingStatus.Draft) {
      return 'APPRAISAL_WAITING_FOR_ACCESS_APPROVAL';
    }

    switch (this.meeting.EmployeeProgressStatus) {
      case AppraisalMeetingEmployeeProgressStatus.Submitted:
        return 'APPRAISAL_WAITING_FOR_MANAGER';
      case AppraisalMeetingEmployeeProgressStatus.InProgress:
        return 'APPRAISAL_SAVED_FOR_LATER';
      default:
        return 'NOT_STARTED';
    }
  }

  getEmployeeProgressClass(): string {
    if (!this.meeting) {
      return 'badge-secondary';
    }

    if (this.meeting.Status === AppraisalMeetingStatus.Completed) {
      return 'badge-success';
    }

    if (this.meeting.Status === AppraisalMeetingStatus.Cancelled) {
      return 'badge-danger';
    }

    if (this.meeting.Status === AppraisalMeetingStatus.Draft) {
      return 'badge-warning';
    }

    switch (this.meeting.EmployeeProgressStatus) {
      case AppraisalMeetingEmployeeProgressStatus.Submitted:
        return 'badge-warning';
      case AppraisalMeetingEmployeeProgressStatus.InProgress:
        return 'badge-primary';
      default:
        return 'badge-secondary';
    }
  }

  getRequiredProgressText(): string {
    if (!this.meeting) {
      return '0/0';
    }

    return `${this.meeting.EmployeeAnsweredRequiredQuestionCount}/${this.meeting.EmployeeRequiredQuestionCount}`;
  }

  getEmployeeProgressHintKey(): string {
    if (!this.meeting) {
      return 'APPRAISAL_PROGRESS_HINT_NOT_STARTED';
    }

    if (this.meeting.Status === AppraisalMeetingStatus.Completed) {
      return 'APPRAISAL_PROGRESS_HINT_COMPLETED';
    }

    if (this.meeting.Status === AppraisalMeetingStatus.Cancelled) {
      return 'APPRAISAL_PROGRESS_HINT_CANCELLED';
    }

    if (this.meeting.Status === AppraisalMeetingStatus.Draft) {
      return 'APPRAISAL_MEETING_WAITING_FOR_ACCESS';
    }

    switch (this.meeting.EmployeeProgressStatus) {
      case AppraisalMeetingEmployeeProgressStatus.Submitted:
        return 'APPRAISAL_PROGRESS_HINT_WAITING_FOR_MANAGER';
      case AppraisalMeetingEmployeeProgressStatus.InProgress:
        return 'APPRAISAL_PROGRESS_HINT_SAVED_FOR_LATER';
      default:
        return 'APPRAISAL_PROGRESS_HINT_NOT_STARTED';
    }
  }

  getEmployeeProgressTimestamp(): string | undefined {
    if (!this.meeting) {
      return undefined;
    }

    if (this.meeting.Status === AppraisalMeetingStatus.Completed) {
      return this.toUtcTimestamp(this.meeting.CompletedDate);
    }

    switch (this.meeting.EmployeeProgressStatus) {
      case AppraisalMeetingEmployeeProgressStatus.Submitted:
        return this.toUtcTimestamp(this.meeting.EmployeeSubmittedAt);
      case AppraisalMeetingEmployeeProgressStatus.InProgress:
        return this.toUtcTimestamp(this.meeting.EmployeeLastSavedAt);
      default:
        return undefined;
    }
  }

  toUtcTimestamp(value?: string): string | undefined {
    if (!value?.trim()) {
      return undefined;
    }

    const normalizedValue = value.trim();
    return this.timezoneSuffixPattern.test(normalizedValue)
      ? normalizedValue
      : `${normalizedValue}Z`;
  }

  getEmployeeProgressTimestampLabelKey(): string {
    if (!this.meeting) {
      return 'APPRAISAL_LAST_SAVED';
    }

    if (this.meeting.Status === AppraisalMeetingStatus.Completed) {
      return 'COMPLETED_DATE';
    }

    return this.meeting.EmployeeProgressStatus === AppraisalMeetingEmployeeProgressStatus.Submitted
      ? 'APPRAISAL_SUBMITTED_ON'
      : 'APPRAISAL_LAST_SAVED';
  }

  showTaskCreationInfo(): boolean {
    return ( !!this.meeting &&
      this.canManageFollowUpTasks() &&
      !this.canCreateFollowUpTasks()
    );
  }

  showEmployeeTaskAvailabilityInfo(): boolean {
    return ( !!this.meeting &&
      this.isEmployee &&
      !this.hasFollowUpTasks() &&
      this.meeting.Status !== AppraisalMeetingStatus.Draft &&
      this.meeting.Status !== AppraisalMeetingStatus.Cancelled
    );
  }

  getTaskCreationInfoKey(): string {
    if (this.meeting?.Status === AppraisalMeetingStatus.Cancelled) {
      return 'APPRAISAL_TASKS_DISABLED_FOR_CANCELLED';
    }

    return 'APPRAISAL_ACTION_POINTS_AVAILABLE_AFTER_SUBMISSION';
  }

  isQuestionMissingRequired(question: AppraisalMeetingQuestionDto): boolean {
    if (!this.isQuestionRequiredForCurrentRespondent(question)) return false;
    if (!this.showValidationErrors && !this.touchedQuestions.has(question.QuestionId)) return false;
    return !this.hasResponseValue(this.responseDrafts[question.QuestionId]);
  }

  isQuestionRequiredForCurrentRespondent(question: AppraisalMeetingQuestionDto): boolean {
    return this.isQuestionRequiredForEmployeeRespondent(question);
  }

  onQuestionTouched(questionId: number): void {
    this.touchedQuestions.add(questionId);
    this.handleEmployeeSectionProgress(questionId);
  }

  onBack(): void {
    this.navigateToAppraisalList();
  }

  private confirmMeetingCompletion(): void {
    if (!this.meeting || this.isSaving || this.isUpdatingStatus) {
      return;
    }

    if (this.requiresSignature() && !this.isBothPartiesSigned()) {
      this.notifications.add({ severity: 'error', summary: this.translate.instant('APPRAISAL_CANNOT_COMPLETE'), detail: this.translate.instant('APPRAISAL_COMPLETE_REQUIRES_BOTH_SIGNATURES') });
      return;
    }

    this.confirmationService.confirm({
      message: this.translate.instant('CONFIRM_COMPLETE_APPRAISAL_MEETING'),
      accept: () => this.saveManagerResponsesAndCompleteMeeting()
    });
  }

  private confirmMeetingStatusChange(status: AppraisalMeetingStatus): void {
    if (!this.meeting || this.isUpdatingStatus || this.isSaving) {
      return;
    }

    this.confirmationService.confirm({
      message: this.translate.instant('CONFIRMATION_MESSAGE_TO_CHANGE_STATUS'),
      accept: () => this.updateMeetingStatusAndReturnToList(status)
    });
  }

  private updateMeetingStatusAndReturnToList(status: AppraisalMeetingStatus): void {
    if (!this.meeting) {
      return;
    }

    this.isUpdatingStatus = true;
    this.pendingStatusValue = status;

    this.meetingService.updateStatus(this.meeting.Id, { Status: status }).subscribe({
      next: () => {
        this.isUpdatingStatus = false;
        this.pendingStatusValue = null;
        this.notifications.success('UI_NOTIFICATIONS.SAVED');
        this.navigateToAppraisalList();
      },
      error: (err: Error) => {
        this.isUpdatingStatus = false;
        this.pendingStatusValue = null;
        this.showStatusError(err);
      }
    });
  }

  private showStatusError(err: Error): void {
    const message = err?.message?.trim();
    if (message) {
      this.notifications.add({ severity: 'error', summary: this.translate.instant('APPRAISAL_CANNOT_COMPLETE'), detail: message });
      return;
    }
    this.notifications.success('UI_NOTIFICATIONS.DELETED');
  }

  private saveManagerResponsesAndCompleteMeeting(): void {
    if (!this.meeting || this.isSaving || this.isUpdatingStatus) {
      return;
    }

    const meetingId = this.meeting.Id;
    const shouldSaveManagerResponses = this.canSaveManagerAnswers();
    const responsePayload = shouldSaveManagerResponses ? this.buildResponsePayload() : [];

    this.isSaving = true;
    this.isUpdatingStatus = true;
    this.pendingStatusValue = AppraisalMeetingStatus.Completed;

    const saveResponses$ = shouldSaveManagerResponses
      ? this.meetingService.saveResponses(meetingId, responsePayload)
      : of(void 0);

    saveResponses$
      .pipe(
        switchMap(() =>
          this.meetingService.updateStatus(meetingId, {
            Status: AppraisalMeetingStatus.Completed
          })
        )
      )
      .subscribe({
        next: () => {
          this.isSaving = false;
          this.isUpdatingStatus = false;
          this.pendingStatusValue = null;
          this.notifications.success('UI_NOTIFICATIONS.SAVED');
          this.navigateToAppraisalList();
        },
        error: (err: Error) => {
          this.isSaving = false;
          this.isUpdatingStatus = false;
          this.pendingStatusValue = null;
          this.showStatusError(err);
        }
      });
  }

  private navigateToAppraisalList(): void {
    this.router.navigate(['../../'], {
      relativeTo: this.route,
      queryParams: { tab: 'list' }
    });
  }
}
